import { Box, IconButton, Stack, Typography } from '@mui/material'
import {
  BookingsPaginatedModel,
  FilterInput,
  FilterOperator,
  FilterTypes,
  OrderDirection,
  PaginationInput,
  useBookingsPaginatedQuery,
  useTourTitleQuery
} from '@typings/graphql'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro'
import BaseDataGrid, { BaseDataGridHandler } from '@shared/components/dataGrid/BaseDataGrid'
import { DATA_GRID_PAGE_SIZE } from '@shared/components/dataGrid/constants/dataGrid'
import { themeIcons } from '@theme/index'
import { can } from '@utils/permissions'
import { BaseColumnType } from '@shared/components/dataGrid/types/types'

import { PaymentHistoryDetailsModal } from '../components/PaymentHistoryDetails'
import { tourTitleColumn } from '../dataGrid/columns/tourTitleColumn'
import { dataGridColumns } from '../dataGrid/DataGridColumns'
import { ExportBookings } from '../components/ExportBookings'

const STATIC_FILTER = {
  field: 'voucher',
  value: false,
  type: FilterTypes.Boolean,
  operator: FilterOperator.Is
}

export type PaginatedBookingNode = Exclude<BookingsPaginatedModel['nodes'], null | undefined>[0]
export type PaginatedBookingNodePayment = Exclude<PaginatedBookingNode['payment'], null | undefined>
export type PaginatedBookingNodeTour = Exclude<PaginatedBookingNode['tour'], null | undefined>
export type PaginatedBookingRowModel = Omit<PaginatedBookingNode, '__typename'>
  & Partial<Omit<PaginatedBookingNodePayment, '__typename'>> & Partial<Omit<PaginatedBookingNodeTour, '__typename'>>

export type PaginatedBookingColumnType = Omit<BaseColumnType<PaginatedBookingRowModel>, 'field'> & {
  field: keyof PaginatedBookingRowModel | 'action',
  adminOnly?: boolean
  hideInPanel?: boolean
}

type Props = {
  id?: string
}

const PaymentHistoryTourViewContent: React.FC<Props> = ({ id }) => {
  const { t } = useTranslation()

  const dataGridRef = React.useRef<BaseDataGridHandler>(null)

  const [selectedRow, setSelectedRow] = React.useState<string>('')
  const [showDetailsOverlay, setShowDetailsOverlay] = React.useState<boolean>(false)

  const { data: tourData } = useTourTitleQuery({
    variables: {
      id: id as string
    },
    skip: !id
  })

  const { data, loading, refetch } = useBookingsPaginatedQuery({
    fetchPolicy: 'network-only',
    variables: {
      ...(id && { tourId: id as string }),
      data: {
        limit: DATA_GRID_PAGE_SIZE,
        offset: 0,
        filter: [STATIC_FILTER],
        orderBy:
        {
          field: 'createdAt',
          direction: OrderDirection.Desc
        }
      }
    }
  })

  const handleShowDetails = (code: string) => {
    setSelectedRow(code)
    setShowDetailsOverlay(true)
  }

  const columnData = React.useMemo<PaginatedBookingColumnType[]>(() => {
    const columnVisibility: GridColumnVisibilityModel = {}

    const codeColumn = dataGridColumns.find((column) => column.field === 'code') as PaginatedBookingColumnType
    const restColumns = dataGridColumns.filter((column) => column.field !== 'code')

    const filledColumnData: PaginatedBookingColumnType[] = [codeColumn,
      ...(!id ? [tourTitleColumn] : []), ...restColumns.filter((column) => {
        if (column.hideInPanel) {
          columnVisibility[column.field] = false
        }
        return !column.adminOnly || can('scope:admin')
      }), {
        field: 'action',
        width: 100,
        filterable: false,
        hideable: false,
        renderHeader: () => null,
        renderCell: (params) => {
          return (
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              width: '100%'
            }}
            >
              <IconButton onClick={() => handleShowDetails(params.row.code) }>
                <themeIcons.Visibility />
              </IconButton>
            </Box>
          )
        }

      }]

    return filledColumnData.map((column) => {
      return {
        headerName: t(`payment.${column.field}`),
        disableColumnMenu: true,
        ...column
      }
    })
  }, [])

  const mappedRows = React.useMemo<PaginatedBookingRowModel[]>(() => {
    return data?.bookingsPaginated.nodes?.map((node) => ({
      id: node.code,
      ...node
    }) as PaginatedBookingRowModel) || []
  }, [data])

  const handleFetchMore = React.useCallback(async (variable: PaginationInput) => {
    return await refetch({
      data: {
        ...variable,
        filter: [STATIC_FILTER, ...(variable.filter || [])]
      }
    })
  }, [refetch])

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4, lg: 6 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{
        flex: 0,
        pb: 4,
        display: 'flex',
        justifyContent: 'space-between'
      }}
      >
        <Stack
          spacing={2}
          sx={{
            flexDirection: { xs: 'column', sm: 'row' }
          }}
          justifyContent="space-between"
        >
          {tourData?.tour.keyvisual?.url && (
          <Box sx={{
            width: '150px',
            aspectRatio: '1 / 1',
            borderRadius: '12px',
            backgroundImage: `url(${tourData?.tour.keyvisual?.url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: { xs: 'none', sm: 'block' },
            ml: 2
          }}
          />
          )}
          <Box>
            <Typography variant='h4'>{id ? tourData?.tour.title : t('payment.allTours')}</Typography>
            <Typography>{id ? t('payment.listOfBookings') : t('payment.allToursSubtitle')}</Typography>
          </Box>
        </Stack>
        <Box>
          <ExportBookings
            filter={[STATIC_FILTER, ...(dataGridRef.current?.refetchVariables.filter || [])] as FilterInput[]}
            total={data?.bookingsPaginated.totalCount}
            {...(id && { tourId: id })}
          />
        </Box>
      </Box>
      <Box sx={{
        flex: 1,
        overflow: 'hidden'
      }}
      >
        <BaseDataGrid
          ref={dataGridRef}
          loading={loading}
          gridKey='user'
          columns={columnData}
          serverSide
          refetch={handleFetchMore}
          totalRows={data?.bookingsPaginated.totalCount}
          pageInfo={data?.bookingsPaginated.pageInfo}
          rows={mappedRows}
        />
      </Box>
      <PaymentHistoryDetailsModal
        code={selectedRow}
        open={showDetailsOverlay}
        onClose={() => setShowDetailsOverlay(false)}
      />
    </Box>
  )
}

export const PaymentHistoryTourView = () => {
  const { id } = useParams()

  return <PaymentHistoryTourViewContent {...(id !== 'all-tours' && { id })} />
}
